import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Nav,
  Tab,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './tasklist.css';
import './addtask.css';
import '../users/userlist.css';
import '../../css/SearchFilterPanel.css';
import './performanceDashboard.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from '../../services/AxiosInstance';
import {
  PERIOD_KEYS,
  scoreColorClass,
  OVERVIEW_PERIOD_OPTIONS,
  DEFAULT_OVERVIEW_PERIOD_KEY,
  getMeRowClassName,
  PerformanceBadgePills,
} from './performanceShared';
import CommonTable from '../components/CommonTable/CommonTable';
import {
  getMyAtRisk,
  getTeamPeriodScores,
} from '../../services/PerformanceService';

const PerformanceDetails = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('team');
  const [teamLoading, setTeamLoading] = useState(false);
  const [atRiskLoading, setAtRiskLoading] = useState(false);

  const [atRisk, setAtRisk] = useState({ overdue: [], due_soon: [] });
  const [teamPeriodScores, setTeamPeriodScores] = useState(null);
  const [atRiskLoaded, setAtRiskLoaded] = useState(false);

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ department_id: '', branch_id: '' });

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem('user') || '{}'),
    []
  );
  const currentUserId = userData.id ?? userData.userId ?? userData.corporateUserId;

  const loadAtRisk = useCallback(async () => {
    setAtRiskLoading(true);
    try {
      const atRiskRes = await getMyAtRisk();
      setAtRisk(atRiskRes.data?.data ?? { overdue: [], due_soon: [] });
      setAtRiskLoaded(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to load at-risk tasks');
    } finally {
      setAtRiskLoading(false);
    }
  }, []);

  const loadTeamPeriodScores = useCallback(async (appliedFilters) => {
    setTeamLoading(true);
    try {
      const params = {};
      if (appliedFilters.department_id) params.department_id = appliedFilters.department_id;
      if (appliedFilters.branch_id) params.branch_id = appliedFilters.branch_id;
      const res = await getTeamPeriodScores(params);
      setTeamPeriodScores(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to load team scores');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const corpId = userData.corporateId;
        if (!corpId) return;
        const [bRes, dRes] = await Promise.all([
          axiosInstance.get(`/settings/branches?corporate_id=${corpId}`),
          axiosInstance.get(`/settings/departments?corporate_id=${corpId}`),
        ]);
        setBranches(bRes.data?.data || bRes.data || []);
        setDepartments(dRes.data?.data || dRes.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchFilters();
  }, [userData.corporateId]);

  useEffect(() => {
    if (activeTab === 'team') {
      loadTeamPeriodScores(filters);
    } else if (activeTab === 'at-risk' && !atRiskLoaded) {
      loadAtRisk();
    }
  }, [activeTab, filters, loadTeamPeriodScores, loadAtRisk, atRiskLoaded]);

  const getDeptBranchId = (dept) => dept?.branch_id ?? dept?.BranchId ?? dept?.branchId;

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => String(b.id) === String(branchId));
    return branch?.name || branch?.branch_name || null;
  };

  const getDepartmentLabel = (dept) => {
    const deptName = dept.name || dept.department_name || 'Department';
    const branchName = getBranchName(getDeptBranchId(dept));
    return branchName ? `${deptName} (${branchName})` : deptName;
  };

  const filteredDepartments = useMemo(() => {
    if (!filters.branch_id) return departments;
    return departments.filter(
      (d) => String(getDeptBranchId(d)) === String(filters.branch_id)
    );
  }, [departments, filters.branch_id, branches]);

  const handleBranchFilterChange = (branchId) => {
    setFilters((prev) => {
      const next = { ...prev, branch_id: branchId };
      if (branchId && prev.department_id) {
        const dept = departments.find((d) => String(d.id) === String(prev.department_id));
        if (dept && String(getDeptBranchId(dept)) !== String(branchId)) {
          next.department_id = '';
        }
      }
      return next;
    });
  };

  const formatUserBranchDepartment = (row) => {
    const parts = [row.branch, row.department].filter(
      (part) => part && part !== 'No Department'
    );
    return parts.length ? parts.join(' · ') : null;
  };

  const overdueCount = atRiskLoaded ? (atRisk.overdue?.length || 0) : null;

  const periodShortLabel = (key) =>
    OVERVIEW_PERIOD_OPTIONS.find((o) => o.key === key)?.shortLabel ||
    teamPeriodScores?.periods?.[key]?.label?.slice(0, 14) ||
    key.replace(/_/g, ' ');

  const teamColumns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        headerStyle: { width: '30%' },
        cellStyle: { width: '30%' },
        render: (row) => (
          <div className="performance-team-name-cell">
            <div className="fw-semibold text-truncate" title={row.full_name}>
              {row.full_name}
            </div>
            {formatUserBranchDepartment(row) ? (
              <small
                className="text-muted d-block text-truncate"
                title={formatUserBranchDepartment(row)}
              >
                {formatUserBranchDepartment(row)}
              </small>
            ) : null}
          </div>
        ),
      },
      ...PERIOD_KEYS.map((key) => ({
        key,
        header: periodShortLabel(key),
        headerStyle: { width: '14%', textAlign: 'center' },
        cellStyle: { width: '14%', textAlign: 'center' },
        cellClassName: 'performance-score-cell',
        render: (row) => {
          const s = row.scores?.[key];
          const sc = s?.performance_score;
          if (sc == null) return <span className="text-muted">—</span>;
          return (
            <span
              className={`performance-score-badge ${scoreColorClass(s?.color?.color)}`}
            >
              {sc}
            </span>
          );
        },
      })),
      {
        key: 'badges',
        header: 'Badges',
        headerStyle: { width: '28%' },
        cellStyle: { width: '28%' },
        render: (row) => (
          <PerformanceBadgePills
            badges={row.badges}
            emptyPlaceholder={<span className="text-muted">—</span>}
          />
        ),
      },
    ],
    [teamPeriodScores]
  );

  return (
    <Row className="performance-overview-page performance-container performance-compact performance-theme" style={{ marginBottom: '0px !important' }}>
      <ToastContainer position="top-right" autoClose={4000} />

      <Col lg={12}>
        <button
          type="button"
          className="back-button"
          onClick={() => navigate('/task/performance')}
        >
          <i className="fas fa-arrow-left" style={{ marginRight: '0.5rem' }} aria-hidden />
          Back
        </button>

        <Card className="user-list-card performance-details-card">
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'team')}>
            <Card.Header className="performance-card-header performance-details-card-header">
              <Nav variant="tabs" className="performance-tabs mb-0">
                <Nav.Item>
                  <Nav.Link eventKey="team">
                    <i className="fas fa-users me-1" aria-hidden /> Team
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="at-risk">
                    <i className="fas fa-exclamation-triangle me-1" aria-hidden /> At risk
                    {overdueCount > 0 && (
                      <span className="performance-tab-count performance-tab-count--danger ms-1">
                        {overdueCount}
                      </span>
                    )}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            <Card.Body className="performance-card-body">
              {!teamLoading && teamPeriodScores?.computed_through && (
                <p className="performance-period-context mb-2">
                  <i className="fas fa-clock me-1" aria-hidden />
                  <span>Scores computed</span>
                  <span className="performance-period-context__through">
                    · through {teamPeriodScores.computed_through}
                  </span>
                </p>
              )}
              <Tab.Content>
                  <Tab.Pane eventKey="team">
                    <div className="filter-wrapper performance-team-filters">
                      <div className="main-filters">
                        <div className="filter-form">
                          <div className="filter-row">
                            <div className="filter-group">
                              <label htmlFor="performance-filter-branch">Branch</label>
                              <select
                                id="performance-filter-branch"
                                value={filters.branch_id}
                                onChange={(e) => handleBranchFilterChange(e.target.value)}
                              >
                                <option value="">All branches</option>
                                {branches.map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.name || b.branch_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="filter-group">
                              <label htmlFor="performance-filter-department">Department</label>
                              <select
                                id="performance-filter-department"
                                value={filters.department_id}
                                onChange={(e) =>
                                  setFilters((f) => ({ ...f, department_id: e.target.value }))
                                }
                              >
                                <option value="">
                                  {filters.branch_id ? 'All departments in branch' : 'All departments'}
                                </option>
                                {filteredDepartments.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {getDepartmentLabel(d)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {teamLoading ? (
                      <div className="loading-container performance-loading-compact">
                        <div className="loading-spinner" />
                      </div>
                    ) : (
                      <div className="row">
                        <CommonTable
                          className="performance-table-fit"
                          columns={teamColumns}
                          data={teamPeriodScores?.users || []}
                          emptyMessage="No team data found."
                          emptyStateIcon="fas fa-users"
                          getRowId={(row) => row.user_id}
                          getRowClassName={(row) =>
                            getMeRowClassName(
                              row,
                              currentUserId,
                              row.scores?.[DEFAULT_OVERVIEW_PERIOD_KEY]?.color?.color
                            )
                          }
                          maxHeight="70vh"
                          minHeight="320px"
                        />
                      </div>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="at-risk">
                    {atRiskLoading ? (
                      <div className="loading-container performance-loading-compact">
                        <div className="loading-spinner" />
                      </div>
                    ) : (
                      <Row className="g-3">
                        <Col md={6}>
                          <Card className="user-list-card performance-at-risk-card h-100">
                            <Card.Header className="fw-semibold text-danger">
                              Overdue ({atRisk.overdue?.length || 0})
                            </Card.Header>
                            <Card.Body>
                              {(atRisk.overdue || []).length === 0 ? (
                                <p className="text-muted mb-0">No overdue tasks.</p>
                              ) : (
                                atRisk.overdue.map((t) => (
                                  <div
                                    key={t.task_id}
                                    className="at-risk-item"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/task/taskdetails/${t.task_id}`)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') navigate(`/task/taskdetails/${t.task_id}`);
                                    }}
                                  >
                                    <div className="fw-semibold">{t.title}</div>
                                    <small className="text-muted">
                                      Due {t.due_date}
                                      {t.days_overdue != null ? ` · ${t.days_overdue}d overdue` : ''}
                                    </small>
                                  </div>
                                ))
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="user-list-card performance-at-risk-card h-100">
                            <Card.Header className="fw-semibold text-warning">
                              Due soon ({atRisk.due_soon?.length || 0})
                            </Card.Header>
                            <Card.Body>
                              {(atRisk.due_soon || []).length === 0 ? (
                                <p className="text-muted mb-0">Nothing due in the next 3 days.</p>
                              ) : (
                                atRisk.due_soon.map((t) => (
                                  <div
                                    key={t.task_id}
                                    className="due-soon-item"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/task/taskdetails/${t.task_id}`)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') navigate(`/task/taskdetails/${t.task_id}`);
                                    }}
                                  >
                                    <div className="fw-semibold">{t.title}</div>
                                    <small className="text-muted">
                                      Due {t.due_date}
                                      {t.days_until_due != null ? ` · in ${t.days_until_due}d` : ''}
                                    </small>
                                  </div>
                                ))
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}
                  </Tab.Pane>

              </Tab.Content>
            </Card.Body>
          </Tab.Container>
        </Card>
      </Col>

      <InternalFooter />
    </Row>
  );
};

export default PerformanceDetails;
